import React, { useState, useEffect } from 'react';
import { X, DollarSign, Upload, Wallet } from 'lucide-react';
import paymentService from '../../../../api/admin_api/paymentService';
import uploadService from '../../../../api/admin_api/uploadService';
import { toDateOnly } from '../../../../utils/dateOnly';
import Toast from '../../../../utils/Toast';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
];

/**
 * Add Payment as right sidebar. Party-wise: shows party name and order context.
 */
export default function AddPaymentSidebar({ payment, onClose, onSuccess }) {
  const partyName = payment?.customer?.name ?? payment?.paymentData?.party_name ?? '—';
  const quotationId = payment?.quotationIdRaw || payment?.quotationId;
  const quotationNumber = payment?.quotationId ?? '—';
  const customerId = payment?.leadId ?? payment?.paymentData?.lead_id ?? null;
  const dueAmount = Number(payment?.quotationRemainingDue ?? payment?.dueAmount ?? 0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [form, setForm] = useState({
    installment_amount: '',
    payment_date: toDateOnly(new Date()),
    payment_method: 'cash',
    payment_reference: '',
  });
  const [partyCreditBalance, setPartyCreditBalance] = useState(0);
  const [adjustCredit, setAdjustCredit] = useState('no');
  const [creditApplyMode, setCreditApplyMode] = useState('full');
  const [customCreditAmount, setCustomCreditAmount] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const amount = Number(form.installment_amount);
    if (!quotationId || !customerId) {
      setError('Order or customer not found.');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Enter a valid amount.');
      return;
    }
    setLoading(true);
    try {
      let payment_receipt_url;
      if (receiptFile) {
        payment_receipt_url = await uploadService.uploadFile(receiptFile, 'payments');
      }
      const payload = {
        customer_id: customerId,
        quotation_id: quotationId,
        installment_amount: amount,
        payment_method: form.payment_method,
        payment_reference: (form.payment_reference || '').trim() || undefined,
        payment_status: 'completed',
        payment_date: form.payment_date,
        ...(payment_receipt_url && { payment_receipt_url }),
      };
      if (partyCreditBalance > 0 && adjustCredit === 'yes') {
        const raw = creditApplyMode === 'full' ? partyCreditBalance : Math.min(Number(customCreditAmount) || 0, partyCreditBalance);
        const toApply = Math.min(raw, dueAmount);
        if (toApply > 0) {
          payload.adjust_credit = true;
          payload.credit_adjust_amount = toApply;
        }
      }
      const response = await paymentService.createPayment(payload);
      if (response?.data?.success !== false && response?.success !== false) {
        Toast.success('Payment added successfully.');
        onSuccess?.();
        onClose();
      } else {
        setError(response?.data?.message || 'Failed to add payment.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to add payment.';
      setError(msg);
      Toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setMounted(true), 20);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!customerId) {
      setPartyCreditBalance(0);
      return;
    }
    let cancelled = false;
    paymentService.getCustomerCredit(customerId).then((res) => {
      const balance = res?.data?.data?.balance ?? res?.data?.balance ?? 0;
      if (!cancelled) setPartyCreditBalance(Number(balance));
    }).catch(() => { if (!cancelled) setPartyCreditBalance(0); });
    return () => { cancelled = true; };
  }, [customerId]);

  if (!payment) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[100] bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`fixed top-0 right-0 z-[101] w-full max-w-md h-full bg-white shadow-xl flex flex-col transition-transform duration-200 ease-out ${
          mounted ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-label="Add payment"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-600" />
            Add Payment
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-200 text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-100 bg-gray-50/50">
          <p className="text-sm font-medium text-gray-900">Party: {partyName}</p>
          <p className="text-sm text-gray-600 mt-0.5">
            Order: <span className="font-mono">{quotationNumber}</span>
            {dueAmount > 0 && (
              <span className="block text-amber-600">Due: ₹{dueAmount.toLocaleString('en-IN')}</span>
            )}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="p-2 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) *</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              required
              value={form.installment_amount}
              onChange={(e) => setForm((prev) => ({ ...prev, installment_amount: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
            <input
              type="date"
              required
              value={form.payment_date}
              onChange={(e) => setForm((prev) => ({ ...prev, payment_date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
            <select
              value={form.payment_method}
              onChange={(e) => setForm((prev) => ({ ...prev, payment_method: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          {partyCreditBalance > 0 && (
            <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/50 space-y-3">
              <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                <Wallet className="h-4 w-4 text-emerald-600" />
                Party credit: ₹{Number(partyCreditBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-gray-700">Adjust credit against this payment?</p>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="adjustCredit" checked={adjustCredit === 'no'} onChange={() => setAdjustCredit('no')} className="rounded-full" />
                  No
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="adjustCredit" checked={adjustCredit === 'yes'} onChange={() => setAdjustCredit('yes')} className="rounded-full" />
                  Yes
                </label>
              </div>
              {adjustCredit === 'yes' && (
                <div className="pl-2 space-y-2 border-l-2 border-emerald-300">
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="creditMode" checked={creditApplyMode === 'full'} onChange={() => setCreditApplyMode('full')} className="rounded-full" />
                      Full (₹{Number(partyCreditBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })})
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="creditMode" checked={creditApplyMode === 'custom'} onChange={() => setCreditApplyMode('custom')} className="rounded-full" />
                      Custom
                    </label>
                  </div>
                  {creditApplyMode === 'custom' && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">₹</span>
                      <input
                        type="number"
                        min="0"
                        max={partyCreditBalance}
                        step="0.01"
                        value={customCreditAmount}
                        onChange={(e) => setCustomCreditAmount(e.target.value)}
                        className="w-28 px-2 py-1.5 border border-gray-300 rounded text-sm"
                        placeholder="Amount"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reference (optional)</label>
            <input
              type="text"
              value={form.payment_reference}
              onChange={(e) => setForm((prev) => ({ ...prev, payment_reference: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Cheque no, UPI ref, etc."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Receipt / screenshot (optional)</label>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
            />
            {receiptFile && (
              <span className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                <Upload className="w-3 h-3" /> {receiptFile.name}
              </span>
            )}
          </div>
          <div className="flex gap-2 justify-end pt-2 sticky bottom-0 bg-white pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {loading ? 'Adding...' : 'Add Payment'}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}
